import { ColumnDef } from "@tanstack/react-table";
import { fCurrencyUSDExtended } from "@/utils/formatNumber";
import { useGetCredit } from "@/service/payment/hook";
import { useState, useEffect, useRef, useMemo } from "react";
import {
  PaginationState,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
} from "@tanstack/react-table";
import { DataTablePaginationCursor } from "@/components/ui/data-table/DataTablePaginationCursor";
import { DataTableManual } from "@/components/ui/data-table/DataTableManual";
import dayjs from "dayjs";
import { fFullDateTime } from "@/utils/formatTime";
import CopyIconButton from "@/components/ui/copy-to-clipboard/copy-icon-button";

const Credit = () => {
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  
  // Cursor pagination parameters
  const [cursorPagination, setCursorPagination] = useState({
    starting_after: undefined as string | undefined,
    ending_before: undefined as string | undefined,
    has_more: false,
  });

  // Store the previous pagination state to detect changes
  const prevPaginationRef = useRef({ pageIndex, pageSize });

  // Memoize pagination state to avoid unnecessary re-renders
  const pagination = {
    pageIndex,
    pageSize,
  };

  const {
    data: creditList,
    isLoading,
    refetch,
  } = useGetCredit({
    limit: pageSize,
    startAfter: cursorPagination.starting_after || "",
  });

  // Update cursor pagination info when data changes
  useEffect(() => {
    if (creditList) {
      setCursorPagination((prev) => ({
        ...prev,
        has_more: creditList.has_more || false,
      }));
    }
  }, [creditList]);

  const dataCreditList = useMemo(
    () =>
      [...(creditList?.data || [])]
        .sort((a, b) => {
          return (
            new Date(Number(b.expires_at || 0)).getTime() -
            new Date(Number(a.expires_at || 0)).getTime()
          );
        })
        .map((item) => ({
          credit_id: item.id,
          credit_amount: item.amount.monetary.value,
          effective_at: item?.effective_at
            ? dayjs.unix(item?.effective_at).toISOString()
            : undefined,
          expired_at: item?.expires_at
            ? dayjs.unix(item?.expires_at).toISOString()
            : undefined,
          credit_name: item.name,
          voided_at: item?.voided_at
            ? dayjs.unix(item?.voided_at).toISOString()
            : undefined,
        })),
    [creditList?.data]
  );

  // กำหนดคอลัมน์สำหรับ DataTable
  const columns: ColumnDef<{
    credit_amount: number;
    effective_at: string | undefined;
    expired_at: string | undefined;
    credit_name: string;
    credit_id: string;
    voided_at: string | undefined;
  }>[] = [
    {
      accessorKey: "credit_amount",
      header: "Amount",
      size: 100,
      cell: ({ row }) => {
        const amount = parseFloat(row.getValue("credit_amount"));

        return (
          <div className="font-medium">{fCurrencyUSDExtended(amount / 100)}</div>
        );
      },
    },
    {
      accessorKey: "credit_name",
      header: "Credit Name",
      size: 150,
      cell: ({ row }) => {
        const creditName = row.getValue("credit_name") as string;
        return <div className="text-xs truncate max-w-[150px]">{creditName}</div>;
      },
    },
    {
      accessorKey: "effective_at",
      header: "Effective Date",
      size: 120,
      cell: ({ row }) => {
        const dateStr = row.getValue("effective_at") as string;
        return dateStr ? fFullDateTime(dateStr) : "";
      },
    },
    {
      accessorKey: "expired_at",
      header: "Expiry Date",
      size: 120,
      cell: ({ row }) => {
        const dateStr = row.getValue("expired_at") as string;
        return dateStr ? fFullDateTime(dateStr) : "";
      },
    },
    {
      accessorKey: "voided_at",
      header: "Voided Date",
      size: 120,
      cell: ({ row }) => {
        const dateStr = row.getValue("voided_at") as string;
        return dateStr ? fFullDateTime(dateStr) : "";
      },
    },
    {
      accessorKey: "credit_id",
      header: "ID",
      cell: ({ row }) => {
        const creditId = row?.original?.credit_id;
        return (
          <div className="flex items-center">
            <div className="text-xs">{creditId.slice(0, 4) + "..." + creditId.slice(-4)}</div>
            <CopyIconButton text={creditId} />
          </div>
        );
      },
    },
  ];

  // Setup the table
  const table = useReactTable({
    data: dataCreditList,
    columns,
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
  });

  // Handle pagination changes
  useEffect(() => {
    const prevPageIndex = prevPaginationRef.current.pageIndex;
    const prevPageSize = prevPaginationRef.current.pageSize;

    // Skip if this is the same state as before
    if (prevPageIndex === pageIndex && prevPageSize === pageSize) {
      return;
    }

    // Moving to next page
    if (pageIndex > prevPageIndex && dataCreditList.length > 0) {
      const lastItemId = dataCreditList[dataCreditList.length - 1].credit_id;
      setCursorPagination((prev) => ({
        ...prev,
        starting_after: lastItemId,
        ending_before: undefined,
      }));
    }
    // Moving to previous page
    else if (pageIndex < prevPageIndex && dataCreditList.length > 0) {
      const firstItemId = dataCreditList[0].credit_id;
      setCursorPagination((prev) => ({
        ...prev,
        ending_before: firstItemId,
        starting_after: undefined,
      }));
    }
    // Changing page size
    else if (pageSize !== prevPageSize) {
      setCursorPagination((prev) => ({
        ...prev,
        starting_after: undefined,
        ending_before: undefined,
      }));
    }

    // Update the ref with current values
    prevPaginationRef.current = { pageIndex, pageSize };
  }, [pageIndex, pageSize, dataCreditList]);

  // Force refetch when cursor pagination changes
  useEffect(() => {
    if (
      cursorPagination.starting_after !== undefined ||
      cursorPagination.ending_before !== undefined
    ) {
      refetch();
    }
  }, [
    cursorPagination.starting_after,
    cursorPagination.ending_before,
    refetch,
  ]);

  return (
    <div>
      <div className="flex flex-col gap-2">
        <div className="">
          <div className="flex justify-between gap-4">
            <div className="font-semibold mb-2">Credit History</div>
          </div>
          <DataTableManual
            columns={columns}
            table={table}
            isLoading={isLoading}
            isHidePagination
          />
          <div className="py-4">
            {!!table.getRowModel().rows?.length && (
              <DataTablePaginationCursor
                table={table}
                isSelectPageSize={true}
                hasMore={cursorPagination.has_more}
                currentPageIndex={pageIndex}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Credit;
